import { useState, useEffect, useMemo } from 'react';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import LockIcon from '@mui/icons-material/Lock';
import IconButton from '@mui/material/IconButton';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import dayjs from 'dayjs';

import { warehouses, suppliers } from '../../services/dataClient';

const ORIGIN_TYPES = ['FSL', 'Warehouse', 'On-Site', 'Supplier'];

const CARRIERS = ['FedEx', 'UPS', 'DHL', 'USPS', 'OnTrac', 'Freight'];

const EMPTY_FORM = {
  originType:      '',
  originLocation:  '',
  ticketNumber:    '',
  customerName:    '',
  customerAddress: '',
  customerCity:    '',
  customerState:   '',
  customerZip:     '',
  carrier:         'FedEx',
  trackingNumber:  '',
  notes:           '',
};

const activeWarehouses = warehouses.filter((w) => w.active);
const activeSuppliers  = suppliers.filter((s) => s.active);

// Read-only field shown when a value is locked (pre-filled from an order)
function LockedField({ label, value }) {
  return (
    <TextField
      size="small"
      label={label}
      value={value}
      fullWidth
      InputProps={{
        readOnly: true,
        endAdornment: <LockIcon sx={{ fontSize: 14, color: 'var(--grey-400)', mr: '2px' }} />,
      }}
      sx={{ '& .MuiInputBase-root': { backgroundColor: '#f8fafc' },
        '& .MuiInputBase-input': { color: 'var(--grey-600)', WebkitTextFillColor: 'var(--grey-600)' },
      }}
    />
  );
}

/**
 * Reusable Create Shipment modal
 .*
 * @param {{
 *   open: boolean,
 *   onClose: () => void,
 *   onSubmit: (shipment: Object) => void,
 *   prefill?: {
 *     ticketNumber?: string,
 *     originType?: string,      // 'FSL' | 'Warehouse' | 'Supplier'
 *     originLocation?: string,
 *     customerName?: string,
 *     customerAddress?: string,
 *     customerCity?: string,
 *     customerState?: string,
 *     customerZip?: string,
 *   }
 * }} props
 *
 * Fields present in `prefill` are pre-filled AND locked (non-editable)
 .* Fields absent from `prefill` are empty and editable
 .* `onSubmit` receives the complete new shipment object
 .*/
export default function CreateShipmentModal({ open, onClose, onSubmit, prefill }) {
  const [form, setForm] = useState(EMPTY_FORM);

  // Apply prefill and reset editable fields each time the modal opens
  useEffect(() => {
    if (open) {
      setForm({
        ...EMPTY_FORM,
        ticketNumber:    prefill?.ticketNumber    ?? '',
        originType:      prefill?.originType      ?? '',
        originLocation:  prefill?.originLocation  ?? '',
        customerName:    prefill?.customerName    ?? '',
        customerAddress: prefill?.customerAddress ?? '',
        customerCity:    prefill?.customerCity    ?? '',
        customerState:   prefill?.customerState   ?? '',
        customerZip:     prefill?.customerZip     ?? '',
        carrier:         prefill?.carrier         ?? 'FedEx',
      });
    }
  }, [open]); // eslint-disable-line react-hooks/exhaustive-deps

  // Which fields are locked — any key present in prefill
  const locked = prefill ?? {};

  const handleFormChange = (field) => (e) => {
    setForm((prev) => {
      const next = { ...prev, [field]: e.target.value };
      if (field === 'originType') next.originLocation = '';
      return next;
    });
  };

  const isSupplier = form.originType === 'Supplier';

  const locationOptions = useMemo(() => {
    if (!form.originType) return [];
    if (form.originType === 'Supplier')
      return activeSuppliers.map((s) => ({ id: s.id, label: s.name, value: s.name }));
    return activeWarehouses
      .filter((w) => w.type === form.originType)
      .map((w) => ({ id: w.id, label: w.name, value: `${w.name} — ${w.city}, ${w.state}` }));
  }, [form.originType]);

  const isFormValid = form.originType && form.originLocation && form.customerName && form.carrier && form.trackingNumber;

  const handleSubmit = () => {
    const newShipment = {
      id:              `shp-new-${Date.now()}`,
      shipmentNumber:  `SHP-3${String(Date.now()).slice(-4)}`,
      originType:      form.originType,
      originLocation:  form.originLocation,
      vendorName:      isSupplier ? form.originLocation : null,
      vendorReason:    null,
      customerName:    form.customerName,
      customerAddress: [form.customerAddress, form.customerCity, form.customerState, form.customerZip].filter(Boolean).join(', '),
      carrier:         form.carrier,
      trackingNumber:  form.trackingNumber,
      notes:           form.notes,
      status:          'Active',
      createdDate:     dayjs().format('YYYY-MM-DD'),
      partOrderId:     null,   // caller may override
      ticketNumber:    form.ticketNumber || null,
    };
    onSubmit(newShipment);
  };

  const btnSx = {
    backgroundColor: '#009688',
    '&:hover': { backgroundColor: '#00796B' },
    '&.Mui-disabled': { backgroundColor: 'var(--grey-200)', color: 'var(--grey-500)' },
    textTransform: 'none', fontWeight: 600, fontSize: '0.875rem', boxShadow: 'none', px: 2,
  };

  return (
    <Dialog open={open} onClose={(e, reason) => { if (reason !== 'backdropClick') onClose(); }} maxWidth="sm" fullWidth>
      <DialogTitle sx={{ fontWeight: 600, fontSize: '1rem', color: 'var(--grey-900)', pb: 1, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
        Create Shipment
        <IconButton size="small" onClick={onClose} sx={{ color: 'var(--grey-400)', '&:hover': { color: 'var(--grey-700)' }, mr: -1 }}>
          <HighlightOffIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: '12px !important' }}>

        {/* ── Origin row ── */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {locked.originType != null ? (
            <LockedField label="Origin Type" value={form.originType} />
          ) : (
            <FormControl size="small" fullWidth>
              <InputLabel>Origin Type</InputLabel>
              <Select label="Origin Type" value={form.originType} onChange={handleFormChange('originType')}>
                {ORIGIN_TYPES.map((t) => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </Select>
            </FormControl>
          )}

          {locked.originLocation != null ? (
            <LockedField label="Origin Location" value={form.originLocation} />
          ) : (
            <FormControl size="small" fullWidth disabled={!form.originType}>
              <InputLabel>{isSupplier ? 'Supplier' : 'Origin Location'}</InputLabel>
              <Select
                label={isSupplier ? 'Supplier' : 'Origin Location'}
                value={form.originLocation}
                onChange={handleFormChange('originLocation')}
              >
                {locationOptions.map((opt) => (
                  <MenuItem key={opt.id} value={opt.value}>{opt.label}</MenuItem>
                ))}
              </Select>
            </FormControl>
          )}
        </Box>

        {/* ── Linked ticket ── */}
        {locked.ticketNumber != null ? (
          <LockedField label="Linked Part Order Ticket #" value={form.ticketNumber} />
        ) : (
          <TextField
            size="small"
            label="Linked Part Order Ticket # (optional)"
            value={form.ticketNumber}
            onChange={handleFormChange('ticketNumber')}
            fullWidth
          />
        )}

        {/* ── Customer name ── */}
        {locked.customerName != null ? (
          <LockedField label="Customer Name" value={form.customerName} />
        ) : (
          <TextField size="small" label="Customer Name" value={form.customerName} onChange={handleFormChange('customerName')} fullWidth />
        )}

        {/* ── Customer address ── */}
        {locked.customerAddress != null ? (
          <LockedField label="Address" value={form.customerAddress} />
        ) : (
          <TextField size="small" label="Address" value={form.customerAddress} onChange={handleFormChange('customerAddress')} fullWidth />
        )}

        {/* ── City / State / Zip ── */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          {locked.customerCity != null ? (
            <LockedField label="City" value={form.customerCity} />
          ) : (
            <TextField size="small" label="City" value={form.customerCity} onChange={handleFormChange('customerCity')} fullWidth />
          )}
          {locked.customerState != null ? (
            <LockedField label="State" value={form.customerState} />
          ) : (
            <TextField size="small" label="State" value={form.customerState} onChange={handleFormChange('customerState')} sx={{ width: 100, flexShrink: 0 }} />
          )}
          {locked.customerZip != null ? (
            <LockedField label="Zip" value={form.customerZip} />
          ) : (
            <TextField size="small" label="Zip" value={form.customerZip} onChange={handleFormChange('customerZip')} sx={{ width: 110, flexShrink: 0 }} />
          )}
        </Box>

        {/* ── Carrier + Tracking ── */}
        <Box sx={{ display: 'flex', gap: 2 }}>
          <FormControl size="small" fullWidth>
            <InputLabel>Carrier</InputLabel>
            <Select label="Carrier" value={form.carrier} onChange={handleFormChange('carrier')}>
              {CARRIERS.map((c) => <MenuItem key={c} value={c}>{c}</MenuItem>)}
            </Select>
          </FormControl>
          <TextField size="small" label="Tracking #" value={form.trackingNumber} onChange={handleFormChange('trackingNumber')} fullWidth />
        </Box>

        {/* ── Notes ── */}
        <TextField
          size="small"
          label="Notes (optional)"
          value={form.notes}
          onChange={handleFormChange('notes')}
          multiline
          rows={2}
          fullWidth
        />
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 2, gap: 1 }}>
        <Typography
          onClick={onClose}
          sx={{ fontSize: '0.875rem', color: 'var(--grey-500)', cursor: 'pointer', '&:hover': { color: 'var(--grey-700)' } }}
        >
          Cancel
        </Typography>
        <Button
          variant="contained"
          size="small"
          disabled={!isFormValid}
          onClick={handleSubmit}
          sx={btnSx}
        >
          Create Shipment
        </Button>
      </DialogActions>
    </Dialog>
  );
}
